import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { CustomerService } from "../../../services/user/customer.services"
import { handleNotify, TOASTER_POSITION, TOASTER_TYPE } from "../../common/notification/toaster_notify.component";
import DeleteModelPopupComponent from "../common_model_popup/deletemodelpopup.component";
import { handleCopy, handleExcel, handlePDF, handlePrint } from "../../../utils/filedownload";

const CustomersComponent = () => {
    const navigate = useNavigate();
    const selectedStore = useSelector(state => state?.user?.selectedStoreId);
    const [searchText, setSearchText] = useState("");
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [showModal, setShowModal] = useState(false);
    const [customerToDelete, setCustomerToDelete] = useState(null);
    const rowsPerPage = 5;

    // Calculate statistics
    const [totalCustomers, setTotalCustomers] = useState(0);
    const [activeCustomers, setActiveCustomers] = useState(0);
    const [inactiveCustomers, setInactiveCustomers] = useState(0);

    // Dynamic Styling Variables
    // const borderStyle = (percentage) => {
    //     if (percentage >= 70) return 'border-success';
    //     if (percentage >= 30) return 'border-warning';
    //     return 'border-danger';
    // };

    // Fetch data
    useEffect(() => {
        const fetchCustomerData = async () => {
            try {
                setLoading(true);
                const response = await CustomerService.getAllCustomers(selectedStore);
                setData(response);
                // Calculate the statistics
                const total = response.length;
                const active = response.filter(customer => customer.isActive).length;
                const inactive = response.filter(customer => !customer.isActive).length;

                setTotalCustomers(total);
                setActiveCustomers(active);
                setInactiveCustomers(inactive);
            } catch (error) {
                handleNotify("Error fetching customers", TOASTER_TYPE.ERROR, TOASTER_POSITION.TOP_RIGHT);
                setData([]);
            } finally {
                setLoading(false);
            }
        };
        fetchCustomerData();
    }, [selectedStore]);

    const filterData = (data) => {
        return (
            data.filter(row =>
            (
                row.companyName?.toLowerCase().includes(searchText.toLowerCase()) ||
                row.contactName?.toLowerCase().includes(searchText.toLowerCase()) ||
                row.email?.toLowerCase().includes(searchText.toLowerCase()) ||
                row.phoneNumber.toLowerCase().includes(searchText.toLowerCase())
            )
            )
        )
    };

    // Handle pagination
    const indexOfLastRow = currentPage * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;
    const filteredData = filterData(data);
    const currentData = filteredData.slice(indexOfFirstRow, indexOfLastRow);
    const totalPages = Math.ceil(filteredData.length / rowsPerPage);

    const handlePageChange = (page) => {
        if (page < 1 || page > totalPages) return;
        setCurrentPage(page);
    };

    // Handle Edit
    const handleEditClick = (item) => {
        navigate('/customer/edit', { state: { customerId: item?.customerId } });
    };

    // Handle Delete
    const handleDeleteClick = (customer) => {
        setCustomerToDelete(customer);
        setShowModal(true);
    };

    const handleDeleteConfirm = async () => {
        if (customerToDelete) {
            try {
                await CustomerService.deleteCustomer(customerToDelete.customerId);
                const newData = data.filter(customer => customer.customerId !== customerToDelete.customerId);
                setData(newData);

                const newFilteredData = filterData(newData);

                const newTotalPages = Math.ceil(newFilteredData.length / rowsPerPage);

                if (newFilteredData.length % rowsPerPage === 0 && currentPage > 1) {
                    setCurrentPage(currentPage - 1);
                } else if (currentPage > newTotalPages && newTotalPages > 0) {
                    setCurrentPage(newTotalPages);
                }

                setShowModal(false);
            } catch (error) {
                handleNotify(error.message || 'Error while deleting customer.', TOASTER_TYPE.ERROR, TOASTER_POSITION.TOP_RIGHT);
            }
        }
    };

    const handleAddNewClick = () => {
        navigate('/customer/create');
    };

    // Columns for DataTable
    const columns = [
        {
            name: "Company",
            selector: (row) => row.companyName,
            sortable: true,
            width: '20%',
        },
        {
            name: "Contact Person",
            selector: (row) => row.contactName,
            sortable: true,
            width: '15%',
        },
        {
            name: "Email",
            selector: (row) => row.email,
            sortable: true,
            width: '20%',
        },
        {
            name: "Phone",
            selector: (row) => row.phoneNumber,
            sortable: true,
            width: '15%',
        },
        {
            name: "Active",
            selector: (row) => (
                <button className={`btn btn-light radius-30 px-4 ${row.isActive ? 'text-success' : 'text-danger'}`}>
                    {row.isActive === true ? "Active" : "Inactive"}
                </button>
            ),
            sortable: true,
            width: '15%',
        },
        {
            name: "Actions",
            button: true,
            width: '15%',
            cell: (row) => (
                <div className="d-flex justify-content-center gap-2">
                    <button className="btn btn-primary" onClick={() => handleEditClick(row)}>
                        Edit
                    </button>
                    <button className="btn btn-danger" onClick={() => handleDeleteClick(row)}>
                        Delete
                    </button>
                </div>
            ),
        },
    ];

    return (
        <>
            <div className="page-wrapper m-0 p-3">
                <div className="page-content">
                    <div className="page-breadcrumb d-flex align-items-center">
                        <div className="breadcrumb-title pe-3 text-primary fw-bold fs-4">
                            Customers
                        </div>
                        <div className="ms-auto">
                            <button className="btn btn-primary" onClick={handleAddNewClick}>
                                New +
                            </button>
                        </div>
                    </div>

                    {/* Customer Stats Cards */}
                    <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-3 g-4 mt-1">
                        <div className="col-12 col-sm-6 col-lg-4">
                            <div className="card radius-10 border-start border-0 border-4 border-warning">
                                <div className="card-body">
                                    <h5 className="text-warning mb-0 d-flex">Total Customers</h5>
                                    <h5 className="d-flex">{totalCustomers}</h5>
                                    <div className="d-flex justify-content-between align-item-center text-primary">
                                        <p className="mb-0 font-13">{((totalCustomers / totalCustomers) * 100).toFixed(2)}% Customer</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-12 col-sm-6 col-lg-4">
                            <div className={`card radius-10 border-start border-0 border-4 border-success ${(activeCustomers)}`}>
                                <div className="card-body">
                                    <h5 className="text-success mb-0 d-flex">Active Customers</h5>
                                    <h5 className="d-flex">{activeCustomers}</h5>
                                    <div className="d-flex justify-content-between align-item-center text-primary">
                                        <p className="mb-0 font-13">{((activeCustomers / totalCustomers) * 100).toFixed(2)}% Active Customer</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-12 col-sm-6 col-lg-4">
                            <div className={`card radius-10 border-start border-0 border-4 border-danger ${(inactiveCustomers)}`}>
                                <div className="card-body">
                                    <h5 className="text-danger mb-0 d-flex">Inactive Customers</h5>
                                    <h5 className="d-flex">{inactiveCustomers}</h5>
                                    <div className="d-flex justify-content-between align-item-center text-primary">
                                        <p className="mb-0 font-13">{((inactiveCustomers / totalCustomers) * 100).toFixed(2)}% Inactive Customers</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Customer DataTable */}
                    <div className="card radius-10 border-top border-0 border-4 border-primary p-4">
                        <div className="card-body">
                            <div className="d-flex flex-column flex-md-row mb-3">
                                <div className="btn-group mb-3 mb-md-0 me-2">
                                    <button className="btn btn-outline-secondary" onClick={() => handleCopy(filteredData)}>Copy</button>
                                    <button className="btn btn-outline-secondary" onClick={() => handleExcel(filteredData)}>Excel</button>
                                    <button className="btn btn-outline-secondary" onClick={() => handlePDF(filteredData)}>PDF</button>
                                    <button className="btn btn-outline-secondary" onClick={handlePrint}>Print</button>
                                </div>

                                <div className="d-flex ms-auto align-items-center">
                                    <div className="me-2">
                                        <span className="fw-bold">Search:</span>
                                    </div>
                                    <input
                                        type="text"
                                        className="form-control w-100 w-md-auto"
                                        placeholder="Search 🔍..."
                                        value={searchText}
                                        onChange={(e) => setSearchText(e.target.value)}
                                    />
                                </div>
                            </div>
                            <div className="table-responsive">
                                <DataTable
                                    columns={columns}
                                    data={currentData}
                                    fixedHeader
                                    responsive
                                    highlightOnHover
                                    sortIcon={<i className="fadeIn animated bx bx-sort" />}
                                    progressPending={loading}
                                />
                            </div>

                            {showModal && (
                                <DeleteModelPopupComponent
                                    show={showModal}
                                    onClose={() => setShowModal(false)}
                                    onConfirm={handleDeleteConfirm}
                                    title="Confirm"
                                    confirmText="Delete"
                                    Message="Are you sure you want to delete this customer?"
                                />
                            )}

                            {/* Pagination Controls */}
                            <div className="d-flex mt-4 justify-content-end">
                                <div className="btn-group">
                                    <button
                                        className="btn btn-outline-secondary"
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        disabled={currentPage === 1}
                                    >
                                        Prev
                                    </button>
                                    {Array.from({ length: totalPages }, (_, index) => (
                                        <button
                                            key={index}
                                            className={`btn ${currentPage === index + 1 ? 'btn-primary' : 'btn-outline-secondary'}`}
                                            onClick={() => handlePageChange(index + 1)}
                                        >
                                            {index + 1}
                                        </button>
                                    ))}
                                    <button
                                        className="btn btn-outline-secondary"
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                    >
                                        Next
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default CustomersComponent;
